import React, { type SyntheticEvent, useCallback, useRef } from 'react';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { type TriggerProps } from '@atlaskit/popup';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import { usePeopleTeamAnalyticsEvents } from '@atlassian/people-teams-ui/analytics';

import PopupComponent from '../../../../common/no-background-popup-component';
import { isTeamBiDirectionalEnabled } from '../../../../common/utils/utils';
import { asyncTeamSelectorEntryPoint } from '../../../team-selector/entrypoint';
import TeamSelectorSkeleton from '../../../team-selector/team-selector-skeleton';
import { TeamButtonTrigger } from '../team-button-trigger';

type TeamButtonComponentProps = {
	containerId: string;
	orgId?: string;
	Wrapper?: React.ElementType;
	numberOfTeams?: number;
};

export const TeamButtonComponent = ({
	numberOfTeams,
	containerId,
	orgId,
	Wrapper = React.Fragment,
}: TeamButtonComponentProps) => {
	const { fireEvent } = usePeopleTeamAnalyticsEvents();
	const triggerRef = useRef<HTMLButtonElement | null>(null);

	const isExperimentEnabled = isTeamBiDirectionalEnabled();

	const handleButtonClick = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const element = event.currentTarget as HTMLElement;
			const isExpanded = isExperimentEnabled
				? element.getAttribute('aria-expanded') === 'true'
				: true;
			if (isExpanded) {
				const result = analyticsEvent?.context.find(
					(item) => item.attributes && item.attributes.scope !== undefined,
				);
				fireEvent('ui.teamButton.clicked', {
					teamsCount: numberOfTeams ?? 0,
					scope: result?.attributes?.scope ?? '',
				});
			}
		},
		[fireEvent, numberOfTeams, isExperimentEnabled],
	);

	const getTriggerProps = useCallback(
		(triggerProps: TriggerProps) => {
			if (!isExperimentEnabled) {
				return triggerProps;
			}
			const setRef = (node: HTMLButtonElement | null) => {
				triggerRef.current = node;
				if (typeof triggerProps.ref === 'function') {
					triggerProps.ref(node);
				} else if (triggerProps.ref) {
					(triggerProps.ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
				}
			};
			return { ...triggerProps, ref: setRef };
		},
		[isExperimentEnabled],
	);

	const onFocusTrigger = useCallback(() => {
		setTimeout(() => {
			if (triggerRef.current) {
				triggerRef.current.focus();
			}
		}, 0);
	}, [triggerRef]);

	if (numberOfTeams === undefined) {
		return null;
	}

	if (numberOfTeams === 0 && !isTeamBiDirectionalEnabled(true)) {
		return null;
	}

	return (
		<ModalContextProvider>
			<PopupTrigger
				entryPoint={asyncTeamSelectorEntryPoint}
				entryPointProps={{
					containerId,
					orgId,
					onModalCloseComplete: onFocusTrigger,
				}}
				entryPointParams={{ containerId }}
				trigger={(triggerProps: TriggerProps) => (
					<Wrapper>
						<TeamButtonTrigger
							triggerProps={getTriggerProps(triggerProps)}
							numberOfTeams={numberOfTeams}
							onClick={handleButtonClick}
						/>
					</Wrapper>
				)}
				fallback={<TeamSelectorSkeleton />}
				placement="bottom-start"
				strategy="absolute"
				popupComponent={PopupComponent}
			/>
		</ModalContextProvider>
	);
};
